import { useEffect, useState } from "react";
import { supabase } from "../lib/supabase.js";
import { useAuth, useReadOnly } from "../lib/auth.js";
import { formatAuPhoneForDisplay } from "../lib/phone.js";
import { Badge, Button, EmptyState } from "../lib/ui.js";
import { CallDetailModal } from "./CallDetail.js";
import { formatDateTime } from "../lib/datetime.js";

export type CallbackStatus = "open" | "contacted" | "closed";

interface CallbackRow {
  id: string;
  reason: string | null;
  preferred_time: string | null;
  status: CallbackStatus;
  created_at: string;
  call_id: string | null;
  customers: { name: string | null; customer_phones: Array<{ e164: string }> | null } | null;
}

/** Who to ring: the customer's first saved number, else the number the call came from. */
export function callbackPhone(row: Pick<CallbackRow, "customers">, callerNumber: string | null | undefined): string {
  const e164 = row.customers?.customer_phones?.[0]?.e164 ?? callerNumber ?? null;
  return e164 ? formatAuPhoneForDisplay(e164) : "-";
}

export function callbackStatusLabel(status: CallbackStatus): string {
  return status === "open" ? "Open" : status === "contacted" ? "Contacted" : "Closed";
}

/**
 * Callback requests the receptionist logged during calls: who to ring, what they need, and the status
 * staff move along (open, contacted, closed). `callback_requests` is readable and writable by business
 * members through RLS (callback_requests_rw); caller numbers come from v_call_list, never `calls`.
 */
export function Callbacks() {
  const { activeBusinessId } = useAuth();
  const [rows, setRows] = useState<CallbackRow[]>([]);
  const [callerByCall, setCallerByCall] = useState<Record<string, string | null>>({});
  const [loading, setLoading] = useState(true);
  const [showClosed, setShowClosed] = useState(false);
  const [openCallId, setOpenCallId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function load() {
    if (!activeBusinessId) return;
    setLoading(true);
    const { data } = await supabase
      .from("callback_requests")
      .select("id, reason, preferred_time, status, created_at, call_id, customers(name, customer_phones(e164))")
      .eq("business_id", activeBusinessId)
      .order("created_at", { ascending: false })
      .limit(100);
    const list = (data ?? []) as unknown as CallbackRow[];
    setRows(list);
    const callIds = list.map((r) => r.call_id).filter((id): id is string => !!id);
    if (callIds.length > 0) {
      const { data: calls } = await supabase.from("v_call_list").select("id, caller_number").in("id", callIds);
      setCallerByCall(Object.fromEntries((calls ?? []).map((c: { id: string; caller_number: string | null }) => [c.id, c.caller_number])));
    }
    setLoading(false);
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeBusinessId]);

  const readOnly = useReadOnly();

  async function setStatus(id: string, status: CallbackStatus) {
    setError(null);
    const { error: err } = await supabase.from("callback_requests").update({ status }).eq("id", id);
    if (err) {
      setError("Could not update this callback. Please try again.");
      return;
    }
    setRows((current) => current.map((r) => (r.id === id ? { ...r, status } : r)));
  }

  if (loading) return <p className="rc-loading">Loading...</p>;

  const shown = showClosed ? rows : rows.filter((r) => r.status !== "closed");

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", flexWrap: "wrap", gap: "4px 16px", marginBottom: 16 }}>
        <h1 className="rc-h1" style={{ margin: 0 }}>Callbacks</h1>
        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: "var(--rc-text-muted)", cursor: "pointer" }}>
          <input type="checkbox" checked={showClosed} onChange={(e) => setShowClosed(e.target.checked)} />
          Show closed
        </label>
      </div>
      {error && <p style={{ color: "var(--rc-danger)", fontSize: 13 }}>{error}</p>}
      {shown.length === 0 ? (
        <EmptyState>{rows.length === 0 ? "No callback requests yet." : "No open callbacks."}</EmptyState>
      ) : (
        <div className="rc-table-scroll">
          <table className="rc-stack" style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
            <thead>
              <tr style={{ textAlign: "left", borderBottom: "1px solid var(--rc-border)", color: "var(--rc-text-muted)" }}>
                <th style={{ padding: "10px 12px" }}>Requested</th>
                <th style={{ padding: "10px 12px" }}>Customer</th>
                <th style={{ padding: "10px 12px" }}>Phone</th>
                <th style={{ padding: "10px 12px" }}>What they need</th>
                <th style={{ padding: "10px 12px" }}>Status</th>
                <th style={{ padding: "10px 12px" }} />
              </tr>
            </thead>
            <tbody>
              {shown.map((row) => (
                <tr key={row.id} style={{ borderBottom: "1px solid var(--rc-border-soft)", verticalAlign: "top" }}>
                  <td data-label="Requested" style={{ padding: "10px 12px", whiteSpace: "nowrap" }}>{formatDateTime(row.created_at)}</td>
                  <td data-label="Customer" style={{ padding: "10px 12px" }}>{row.customers?.name ?? "Unknown"}</td>
                  <td data-label="Phone" style={{ padding: "10px 12px", whiteSpace: "nowrap" }}>{callbackPhone(row, row.call_id ? callerByCall[row.call_id] : null)}</td>
                  <td data-label="What they need" style={{ padding: "10px 12px" }}>
                    {row.reason ?? "-"}
                    {row.preferred_time && (
                      <div style={{ color: "var(--rc-text-muted)", fontSize: 13 }}>Best time: {formatDateTime(row.preferred_time)}</div>
                    )}
                  </td>
                  <td data-label="Status" style={{ padding: "10px 12px" }}>
                    <Badge tone={row.status === "open" ? "warning" : row.status === "contacted" ? "primary" : "neutral"}>{callbackStatusLabel(row.status)}</Badge>
                  </td>
                  <td className="rc-actions" style={{ padding: "10px 12px", whiteSpace: "nowrap" }}>
                    {!readOnly && row.status === "open" && <Button onClick={() => void setStatus(row.id, "contacted")}>Mark contacted</Button>}{" "}
                    {!readOnly && row.status !== "closed" && <Button onClick={() => void setStatus(row.id, "closed")}>Close</Button>}
                    {!readOnly && row.status === "closed" && <Button onClick={() => void setStatus(row.id, "open")}>Reopen</Button>}
                    {row.call_id && (
                      <>
                        {" "}
                        <Button variant="ghost" onClick={() => setOpenCallId(row.call_id)}>Call</Button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {openCallId && <CallDetailModal callId={openCallId} onClose={() => setOpenCallId(null)} />}
    </div>
  );
}
